// Typed API client for all /api/super-admin/* endpoints.
// /api/super-admin/* uses apiFetch — bearer auth (hcm_access_token in sessionStorage).
// /admin/*           uses adminFetch — admin_session httpOnly cookie via admin-api.

import type { TenantConfig, ModuleKey, ModuleState, FieldState } from "@hcm/core";
import { apiFetch, adminFetch } from "./api.js";

// ── Shared types ──────────────────────────────────────────────────────────────

export interface TenantSummary {
  _id:                    string;
  name:                   string;
  plan:                   string;
  status:                 string;
  is_active:              boolean;
  tenant_type?:           string;
  compliance_mode?:       string;
  nsofar_sync_enabled?:   boolean;
  active_agents?:         number;
  last_activity?:         string;
  created_at:             string;
}

export interface ConfigTemplate {
  _id:                      string;
  template_id:              string;
  template_name:            string;
  description:              string;
  created_from_tenant_id:   string | null;
  created_at:               string;
  config:                   TenantConfig;
}

export interface TemplateDiff {
  modulesActivating:      string[];
  modulesDeactivating:    string[];
  fieldsActivating:       string[];
  fieldsDeactivating:     string[];
  fieldsBecomingRequired: string[];
  fieldsBecomingOptional: string[];
}

// ── Tenant listing (admin-api service) ───────────────────────────────────────

export function listTenants(): Promise<{ data: TenantSummary[]; meta: { total: number } }> {
  return adminFetch("/admin/tenants");
}

export function createTenant(body: {
  name: string; plan: string; city?: string; tenant_type?: string;
  compliance_mode?: string; nsofar_sync_enabled?: boolean;
  billing_contact_email?: string;
}): Promise<TenantSummary> {
  return adminFetch("/admin/tenants", { method: "POST", body: JSON.stringify(body) });
}

// ── Config (HCM API super-admin prefix) ───────────────────────────────────────

const SA = "/api/super-admin";

export function getTenantConfig(tenantId: string): Promise<TenantConfig> {
  return apiFetch(`${SA}/tenants/${tenantId}/config`);
}

export function toggleModule(tenantId: string, moduleKey: ModuleKey, active: boolean): Promise<void> {
  return apiFetch(`${SA}/tenants/${tenantId}/config/modules/${moduleKey}`, {
    method: "PUT", body: JSON.stringify({ active }),
  });
}

export function toggleField(
  tenantId: string, moduleKey: ModuleKey, fieldKey: string,
  active: boolean, required: boolean,
): Promise<void> {
  return apiFetch(`${SA}/tenants/${tenantId}/config/fields/${moduleKey}/${fieldKey}`, {
    method: "PUT", body: JSON.stringify({ active, required }),
  });
}

export function updateTenantStatus(tenantId: string, status: string): Promise<void> {
  return apiFetch(`${SA}/tenants/${tenantId}/status`, {
    method: "PUT", body: JSON.stringify({ status }),
  });
}

export function updateNsofarSync(tenantId: string, enabled: boolean): Promise<void> {
  return adminFetch(`/admin/tenants/${tenantId}`, {
    method: "PATCH", body: JSON.stringify({ nsofar_sync_enabled: enabled }),
  });
}

export interface DeleteTenantResult {
  ok: boolean;
  tenant_id: string;
  deleted: Record<string, number>;
  total: number;
}

// Hard-delete a tenant + all its data. Server requires confirm_name === tenant.name
// (typed confirmation) and super-admin; cascades scoped deletes + audits. Irreversible.
export function deleteTenant(tenantId: string, confirmName: string): Promise<DeleteTenantResult> {
  return adminFetch(`/admin/tenants/${tenantId}`, {
    method: "DELETE", body: JSON.stringify({ confirm_name: confirmName }),
  });
}

// ── Templates ─────────────────────────────────────────────────────────────────

export function listTemplates(): Promise<ConfigTemplate[]> {
  return apiFetch(`${SA}/config-templates`);
}

export function createTemplate(
  sourceTenantId: string, templateName: string, description: string,
): Promise<ConfigTemplate> {
  return apiFetch(`${SA}/config-templates`, {
    method: "POST", body: JSON.stringify({ sourceTenantId, templateName, description }),
  });
}

export function applyTemplate(
  tenantId: string, templateId: string, preview: boolean,
): Promise<TemplateDiff & { applied?: boolean }> {
  return apiFetch(`${SA}/tenants/${tenantId}/config/apply-template`, {
    method: "POST", body: JSON.stringify({ templateId, preview }),
  });
}

export type { TenantConfig, ModuleKey, ModuleState, FieldState };
